<template>
  <div class="upCard">
    <!-- Kopf -->
    <div class="upCard__head">
      <div class="upCard__title">
        <span class="upCard__id">Job {{ job.id }}</span>
        <span class="upCard__chip">{{ source }}</span>
        <span v-if="job.mode" class="upCard__chip">{{ job.mode }}</span>
        <span class="upCard__chip" :class="'upCard__chip--' + job.status">{{ statusLabel }}</span>
        <span v-if="scoreMin !== null" class="upCard__chip upCard__chip--score" :style="{ color: scoreColorMin }">
          Score {{ scoreMin }}<template v-if="job.taskScoreLimit"> / Limit {{ job.taskScoreLimit }}</template>
        </span>
      </div>
      <div class="upCard__facts">
        <span v-if="job.taskUid" class="upCard__link" title="Task-ID kopieren" @click="copy(job.taskUid)">Task {{ job.taskUid }}</span>
        <span>{{ time }}</span>
        <span>{{ duration(job.ms) }}</span>
        <span>{{ usd(job.costUsd) }}</span>
        <!-- Farbverschiebung Artwork → Upscale (Mittelwerte je Kanal, 0–255); nach Farb-Lock der Rest -->
        <span v-if="colorShift" class="upCard__shift" :title="colorShiftTitle">Farbe {{ colorShiftLabel }}</span>
        <!-- Ebenen-Modus (RegionLayerEditor) direkt aus dem Lab/der Job-Ansicht — gleiche Komponente wie im LiveEditor -->
        <v-btn v-if="layerRegions.length" small depressed color="#7b5cd6" dark class="upCard__layersBtn" title="Gesichter als Ebenen bearbeiten: verschieben, skalieren, drehen, weich radieren" @click="showLayers = true">
          <v-icon small left>mdi-cursor-move</v-icon>Ebenen
        </v-btn>
        <v-btn icon small @click="$emit('close')"><v-icon small>mdi-close</v-icon></v-btn>
      </div>
    </div>

    <v-dialog v-model="showLayers" fullscreen hide-overlay persistent no-click-animation transition="dialog-bottom-transition">
      <div v-if="showLayers" class="upCard__layerHost">
        <RegionLayerEditor :job="job" :region="layerRegions[0]" :regions="layerRegions" :order-name="job.label || job.id" @close="showLayers = false" @error="onLayerError" />
      </div>
    </v-dialog>

    <div v-if="job.error" class="upCard__error"><v-icon small color="#d8493f" class="mr-1">mdi-alert-circle-outline</v-icon>{{ job.error }}</div>

    <div class="upCard__body">
      <!-- Bild-Stufen -->
      <div class="upCard__viewer">
        <div class="upCard__stages">
          <div
            v-for="s in stageList"
            :key="s.key"
            class="upCard__stage"
            :class="{ 'upCard__stage--active': s.key === activeStageKey }"
            @click="stageKey = s.key"
          >
            <img :src="s.url" alt="" loading="lazy" />
            <span>{{ s.label }}</span>
          </div>
        </div>
        <div v-if="activeStage" class="upCard__canvas">
          <div class="upCard__frame">
            <img :src="activeStage.url" alt="" />
            <template v-if="showBoxes && activeStage.boxes">
              <div
                v-for="r in regions"
                :key="r.id"
                class="upCard__box"
                :style="boxStyle(r)"
                :title="r.label"
              >
                <span :style="{ background: color(r.score) }">{{ r.score === null ? "–" : r.score }}</span>
              </div>
            </template>
          </div>
          <div class="upCard__viewerBar">
            <span>{{ activeStage.label }}<template v-if="activeStage.size"> · {{ activeStage.size }}</template></span>
            <span v-if="regions.length" class="upCard__link" @click="showBoxes = !showBoxes">
              <v-icon x-small>{{ showBoxes ? "mdi-eye-off-outline" : "mdi-eye-outline" }}</v-icon> Regionen
            </span>
            <a :href="activeStage.url" target="_blank" rel="noopener" class="upCard__link"><v-icon x-small>mdi-open-in-new</v-icon> Original öffnen</a>
          </div>
        </div>
        <div v-else class="upCard__empty">Noch kein Bild — der Upscale läuft.</div>
      </div>

      <!-- Bewertung -->
      <div class="upCard__report">
        <!-- Freistellen fehlgeschlagen: Schwarz-Twin lieferte wieder weißen Hintergrund, Bild blieb deckend -->
        <div v-if="job.cutoutFailed" class="upCard__block upCard__block--warn">
          <div class="upCard__blockTitle"><v-icon x-small color="#d8493f" class="mr-1">mdi-alert</v-icon>Freistellen fehlgeschlagen</div>
          <div>{{ job.cutoutError || job.warning || "Hintergrund blieb auch nach Wiederholung deckend." }}</div>
          <div v-if="cutoutStats" class="upCard__muted">transparent {{ cutoutStats.transparentPct }} % · Papier {{ cutoutStats.paperPct }} % · Auto-Import ausgeschlossen, bitte manuell prüfen</div>
          <!-- Nachlauf (followUps.ts): Freisteller wird automatisch nachgeholt -->
          <div v-if="followUpLine" class="upCard__muted">{{ followUpLine }}</div>
        </div>

        <div v-if="job.taskScore && scoreMin !== null" class="upCard__block">
          <div class="upCard__blockTitle">Task-Score</div>
          <div class="upCard__scoreRow">
            <span class="upCard__bigScore" :style="{ color: scoreColorMin }">{{ scoreMin }}</span>
            <div>
              <div>begrenzt durch <b>{{ job.taskScore.limitedBy }}</b></div>
              <div class="upCard__muted">Durchschnitt {{ job.taskScore.avg }} · {{ job.taskScore.count }} Bewertungen</div>
              <div v-if="job.taskScoreLimit" :style="{ color: scoreColorMin }">
                Limit {{ job.taskScoreLimit }}: {{ job.passed ? "erreicht" : "nicht erreicht" }}
              </div>
            </div>
          </div>
        </div>

        <div v-if="analysis" class="upCard__block">
          <div class="upCard__blockTitle">Stil &amp; Inhalt</div>
          <div class="upCard__kv"><span>Stil-Treue</span><b :style="{ color: color(analysis.styleScore) }">{{ show(analysis.styleScore) }}</b></div>
          <div class="upCard__kv"><span>Inhaltstreue</span><b :style="{ color: color(analysis.contentScore) }">{{ show(analysis.contentScore) }}</b></div>
          <ul v-if="contentIssues.length" class="upCard__list upCard__list--warn">
            <li v-for="(issue, i) in contentIssues" :key="'ci' + i">{{ issue }}</li>
          </ul>
          <ul v-if="styleNotes.length" class="upCard__list">
            <li v-for="(note, i) in styleNotes" :key="'sn' + i">{{ note }}</li>
          </ul>
        </div>

        <div v-if="regions.length" class="upCard__block">
          <div class="upCard__blockTitle">Regionen</div>
          <div v-for="r in regions" :key="r.id" class="upCard__region">
            <div class="upCard__regionHead">
              <span class="upCard__chip">{{ r.type }}</span>
              <b class="upCard__regionLabel">{{ r.label }}</b>
              <span class="upCard__regionScore">
                <span :style="{ color: color(r.scoreBefore) }">{{ show(r.scoreBefore) }}</span>
                <template v-if="r.scoreAfter !== null"> → <b :style="{ color: color(r.scoreAfter) }">{{ r.scoreAfter }}</b></template>
              </span>
            </div>
            <div class="upCard__crops">
              <figure v-if="r.crops.original"><img :src="r.crops.original" alt="" loading="lazy" /><figcaption>Foto</figcaption></figure>
              <figure v-if="r.crops.before"><img :src="r.crops.before" alt="" loading="lazy" /><figcaption>Upscale</figcaption></figure>
              <figure v-if="r.crops.after"><img :src="r.crops.after" alt="" loading="lazy" /><figcaption>Nach Pass</figcaption></figure>
            </div>
            <div v-if="r.skipped" class="upCard__muted">{{ r.skipped }}</div>
            <ul v-if="r.deviations.length" class="upCard__list">
              <li v-for="(d, i) in r.deviations" :key="r.id + 'd' + i">{{ d }}</li>
            </ul>
          </div>
        </div>

        <div v-if="steps.length" class="upCard__block">
          <div class="upCard__blockTitle">Ablauf</div>
          <div v-for="(s, i) in steps" :key="'st' + i" class="upCard__kv"><span>{{ s.step }}</span><b>{{ duration(s.ms) }}</b></div>
          <div v-if="job.step" class="upCard__kv"><span><v-progress-circular indeterminate size="11" width="2" class="mr-1" />{{ job.step }}</span><b>läuft</b></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { formatDuration, formatUsd, jobRegions, jobStatusLabel, jobTime, scoreColor, sourceLabel, formatColorShift } from "@/upscaleJobs";
import RegionLayerEditor from "@/components/Upscales/RegionLayerEditor.vue";
import { EventBus, events } from "../../../event-bus";

/** Große Ansicht eines Upscale-Jobs: Stufen, Regionen mit Vorher/Nachher, Scores, Hinweise, Ablauf */
export default {
  name: "UpscaleJobCard",
  components: { RegionLayerEditor },
  props: {
    job: { type: Object, required: true },
  },
  data() {
    return { stageKey: null, showBoxes: true, showLayers: false };
  },
  computed: {
    /** Zustand des automatischen Freisteller-Nachlaufs (job.cutoutFollowUp) */
    followUpLine() {
      const fu = this.job && this.job.cutoutFollowUp;
      if (!fu || !fu.state) return "";
      const at = fu.runAfter ? new Date(fu.runAfter).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" }) : "";
      const n = (Number(fu.attempt) || 0) + 1;
      return { scheduled: `Nachlauf ${n} eingeplant${at ? " ab " + at : ""}`, queued: `Nachlauf ${n} wartet`, running: `Nachlauf ${n} läuft …`, done: "Freisteller nachgeholt", failedOpaque: "Nachlauf: Hintergrund blieb deckend", failed: `Nachlauf gescheitert${fu.error ? ": " + fu.error : ""}` }[fu.state] || "";
    },
    colorShift() {
      const u = this.job.stages && this.job.stages.upscale;
      return u && u.colorShift ? u.colorShift : null;
    },
    colorShiftLabel() {
      return formatColorShift(this.colorShift);
    },
    colorShiftTitle() {
      const u = this.job.stages.upscale;
      const after = u.colorShiftAfterLock ? ` · nach Farb-Lock ${u.colorLock} %: ${formatColorShift(u.colorShiftAfterLock)}` : "";
      return `Farbverschiebung des Upscales gegenüber dem Artwork (Mittelwert je Kanal, 0–255): ${this.colorShiftLabel}${after}`;
    },

    limit() {
      return this.job.taskScoreLimit || 85;
    },
    scoreMin() {
      const s = this.job.taskScore;
      return s && s.min !== null && s.min !== undefined ? s.min : null;
    },
    scoreColorMin() {
      return scoreColor(this.scoreMin, this.limit);
    },
    statusLabel() {
      return jobStatusLabel(this.job);
    },
    source() {
      return sourceLabel(this.job.source);
    },
    time() {
      return jobTime(this.job);
    },
    /** Freisteller-Statistik (report.cutout.stats) für den Warnblock */
    cutoutStats() {
      const st = this.job && this.job.report && this.job.report.cutout && this.job.report.cutout.stats;
      return st && st.transparentPct !== undefined ? st : null;
    },
    analysis() {
      return (this.job.report && this.job.report.analysis) || null;
    },
    regions() {
      return jobRegions(this.job);
    },
    /** Bereiche für den Ebenen-Modus: eingesetzte Renders + Vorschläge (übersprungene mit rohem Render); nur fertige Jobs */
    layerRegions() {
      if (!this.job || this.job.status !== "done" || !(this.job.stages && this.job.stages.final && this.job.stages.final.url)) return [];
      return this.regions.filter((r) => (r.replaced && r.rect && r.crops && r.crops.after) || r.suggestion);
    },
    steps() {
      return (Array.isArray(this.job.steps) ? this.job.steps : []).filter((s) => s && s.step);
    },
    styleNotes() {
      return (this.analysis && this.analysis.styleNotes) || [];
    },
    contentIssues() {
      const issues = (this.analysis && this.analysis.contentIssues) || [];
      const kind = { added: "Neu", removed: "Fehlt", changed: "Anders" };
      return issues.map((i) => `${kind[i.kind] || i.kind}: ${i.label}${i.source === "gemini" ? " (Hinweis)" : ""}`);
    },
    stageList() {
      const st = this.job.stages || {};
      const input = this.job.input || {};
      const size = (s) => (s && s.width ? `${s.width} × ${s.height} px` : "");
      const list = [];
      if (input.artwork) list.push({ key: "artwork", label: "Artwork", url: input.artwork });
      (input.photos || []).forEach((url, i) => list.push({ key: "photo" + i, label: (input.photos || []).length > 1 ? `Foto ${i + 1}` : "Foto", url }));
      if (st.upscale) list.push({ key: "upscale", label: "Upscale", url: st.upscale.url, size: size(st.upscale), boxes: true });
      if (st.finalOpaque) list.push({ key: "finalOpaque", label: "Detail-Pass", url: st.finalOpaque.url, size: size(st.finalOpaque), boxes: true });
      if (st.twin) list.push({ key: "twin", label: "Schwarz-Twin", url: st.twin.url, size: size(st.twin) });
      if (st.alpha) list.push({ key: "alpha", label: "Alpha", url: st.alpha.url });
      if (st.final) list.push({ key: "final", label: "Final", url: st.final.url, size: size(st.final), boxes: true });
      return list;
    },
    activeStageKey() {
      if (this.stageKey && this.stageList.some((s) => s.key === this.stageKey)) return this.stageKey;
      const preferred = ["final", "finalOpaque", "upscale", "artwork"].find((k) => this.stageList.some((s) => s.key === k));
      return preferred || null;
    },
    activeStage() {
      return this.stageList.find((s) => s.key === this.activeStageKey) || null;
    },
  },
  methods: {
    onLayerError(message) {
      EventBus.$emit(events.SNACKBAR, { message: `Ebene konnte nicht übernommen werden: ${message}`, type: "error" });
    },
    duration: formatDuration,
    usd: formatUsd,
    show(v) {
      return v === null || v === undefined ? "–" : v;
    },
    color(score) {
      return scoreColor(score, this.limit);
    },
    /** Box in Promille des Bildes → CSS */
    boxStyle(r) {
      const b = r.box || {};
      return {
        left: b.x / 10 + "%",
        top: b.y / 10 + "%",
        width: b.width / 10 + "%",
        height: b.height / 10 + "%",
        borderColor: this.color(r.score),
      };
    },
    copy(text) {
      if (navigator.clipboard) navigator.clipboard.writeText(text);
    },
  },
};
</script>

<style lang="scss" scoped>
.upCard__shift {
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.upCard__layerHost {
  position: relative;
  width: 100vw;
  height: 100vh;
  background: #262626;
}
.upCard__layersBtn {
  text-transform: none;
  letter-spacing: 0;
  font-weight: 600;
}
.upCard {
  background: #fff;
  border: 1px solid rgba(123, 92, 214, 0.35);
  border-radius: 14px;
  overflow: hidden;
  font-size: 12.5px;
  color: rgba(0, 0, 0, 0.8);

  &__head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    padding: 10px 14px;
    background: #f7f5fc;
    flex-wrap: wrap;
  }
  &__title,
  &__facts {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }
  &__facts {
    color: rgba(0, 0, 0, 0.6);
  }
  &__id {
    font-weight: 700;
    font-size: 13px;
  }
  &__chip {
    padding: 1px 9px;
    border-radius: 999px;
    background: #edeafa;
    color: #6a4fc4;
    font-size: 11px;
    font-weight: 700;

    &--done {
      background: #e2f5ea;
      color: #1f9d61;
    }
    &--failed {
      background: #fde8e6;
      color: #d8493f;
    }
    &--score {
      background: #1c1730;
    }
  }
  &__link {
    cursor: pointer;
    color: #6a4fc4;
    text-decoration: none;
  }
  &__error {
    padding: 8px 14px;
    background: #fde8e6;
    color: #a3342c;
  }
  &__body {
    display: grid;
    grid-template-columns: minmax(0, 1.25fr) minmax(0, 1fr);
    gap: 16px;
    padding: 14px;

    @media (max-width: 960px) {
      grid-template-columns: minmax(0, 1fr);
    }
  }
  &__stages {
    display: flex;
    gap: 8px;
    overflow-x: auto;
    padding-bottom: 8px;
  }
  &__stage {
    flex: 0 0 76px;
    cursor: pointer;
    border-radius: 8px;
    border: 2px solid transparent;
    overflow: hidden;
    background: #f4f3f7;
    text-align: center;
    font-size: 10.5px;

    img {
      width: 100%;
      height: 76px;
      object-fit: cover;
      display: block;
    }
    &--active {
      border-color: #7b5cd6;
    }
  }
  &__canvas {
    border-radius: 10px;
    padding: 10px;
    background: repeating-conic-gradient(#e9e7f0 0% 25%, #f7f6fa 0% 50%) 50% / 20px 20px;
  }
  &__frame {
    position: relative;
    width: fit-content;
    max-width: 100%;
    margin: 0 auto;

    img {
      display: block;
      max-width: 100%;
      max-height: 68vh;
    }
  }
  &__box {
    position: absolute;
    border: 2px solid;
    border-radius: 3px;
    pointer-events: auto;

    span {
      position: absolute;
      top: -9px;
      left: -2px;
      padding: 0 5px;
      border-radius: 4px;
      color: #fff;
      font-size: 10px;
      font-weight: 700;
      line-height: 15px;
    }
  }
  &__viewerBar {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    margin-top: 8px;
    padding: 4px 8px;
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.9);
    font-size: 11.5px;
  }
  &__empty {
    padding: 60px 0;
    text-align: center;
    color: rgba(0, 0, 0, 0.5);
    background: #f4f3f7;
    border-radius: 10px;
  }
  &__report {
    display: flex;
    flex-direction: column;
    gap: 12px;
    max-height: 82vh;
    overflow-y: auto;
  }
  &__block--warn { border: 1px solid rgba(216, 73, 63, 0.4); background: rgba(216, 73, 63, 0.06); border-radius: 8px; padding: 8px 10px; }
  &__block {
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 10px;
    padding: 10px 12px;
  }
  &__blockTitle {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: rgba(0, 0, 0, 0.5);
    margin-bottom: 6px;
  }
  &__scoreRow {
    display: flex;
    align-items: center;
    gap: 14px;
  }
  &__bigScore {
    font-size: 34px;
    font-weight: 800;
    line-height: 1;
  }
  &__muted {
    color: rgba(0, 0, 0, 0.5);
    font-size: 11.5px;
  }
  &__kv {
    display: flex;
    justify-content: space-between;
    gap: 10px;
    padding: 2px 0;
  }
  &__list {
    margin: 6px 0 0;
    padding-left: 16px;
    color: rgba(0, 0, 0, 0.65);
    font-size: 11.5px;

    &--warn {
      color: #a3342c;
    }
  }
  &__region {
    padding: 8px 0;
    border-top: 1px solid rgba(0, 0, 0, 0.06);

    &:first-of-type {
      border-top: 0;
    }
  }
  &__regionHead {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  &__regionLabel {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  &__regionScore {
    white-space: nowrap;
    font-weight: 700;
  }
  &__crops {
    display: flex;
    gap: 6px;
    margin: 6px 0 2px;

    figure {
      flex: 1;
      min-width: 0;
      margin: 0;
      text-align: center;
      font-size: 10.5px;
      color: rgba(0, 0, 0, 0.5);
    }
    img {
      width: 100%;
      max-height: 150px;
      object-fit: contain;
      background: #f4f3f7;
      border-radius: 6px;
      display: block;
    }
  }
}
</style>
